import { z } from 'zod';
import { actorSnapshotSchema, entityIdSchema, nonEmptyTextSchema, resourceScopeSchema, sha256Schema, utcDateTimeSchema, } from '@kop/contracts';
export const workflowStages = [
    'draft',
    'validated',
    'submitted',
    'in-review',
    'returned',
    'resubmitted',
    'approved',
    'action-tracking',
    'closed',
];
export const workflowStageSchema = z.enum(workflowStages);
export const evidenceSchema = z
    .object({
    evidenceId: entityIdSchema,
    manifestId: entityIdSchema,
    contentHash: sha256Schema,
    kind: z.enum([
        'scene',
        'emergency-action',
        'attendance',
        'timeline',
        'improvement',
        'map',
        'attachment',
    ]),
    capturedAt: utcDateTimeSchema,
    caption: nonEmptyTextSchema.max(500),
    containsFace: z.boolean(),
    faceProtection: z.enum(['not-applicable', 'consent-recorded', 'redacted-derivative']),
    serverCommitted: z.boolean(),
    immutable: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.containsFace && value.faceProtection === 'not-applicable')
        context.addIssue({
            code: 'custom',
            path: ['faceProtection'],
            message: 'Face evidence requires consent or a redacted derivative',
        });
});
export const reviewSchema = z
    .object({
    round: z.number().int().positive(),
    submittedAt: utcDateTimeSchema,
    submittedBy: actorSnapshotSchema,
    decision: z.enum(['pending', 'returned', 'approved']),
    decidedAt: utcDateTimeSchema.nullable(),
    decidedBy: actorSnapshotSchema.nullable(),
    opinion: z.string().trim().max(2_000).nullable(),
})
    .strict();
const actionSchema = z
    .object({
    actionId: entityIdSchema,
    description: nonEmptyTextSchema.max(2_000),
    ownerIdentityId: entityIdSchema,
    dueAt: utcDateTimeSchema,
    status: z.enum(['open', 'in-progress', 'verified', 'cancelled']),
    verificationEvidenceIds: z.array(entityIdSchema),
    verifiedAt: utcDateTimeSchema.nullable(),
})
    .strict();
export const reportingStandardSnapshotSchema = z
    .object({
    standardId: entityIdSchema,
    versionId: entityIdSchema,
    title: nonEmptyTextSchema.max(300),
    sourceDocumentId: entityIdSchema,
    sourceRevision: nonEmptyTextSchema.max(100),
    effectiveFrom: z.iso.date(),
    publishedAt: utcDateTimeSchema,
    status: z.literal('published'),
    /** Human-reviewed rule references; deadlines and thresholds are never compiled into this package. */
    ruleReferences: z
        .array(z
        .object({
        ruleId: entityIdSchema,
        label: nonEmptyTextSchema.max(300),
        reportingAuthority: nonEmptyTextSchema.max(300),
        deadlineExpression: nonEmptyTextSchema.max(500),
    })
        .strict())
        .min(1),
})
    .strict();
const incidentClassificationSchema = z
    .object({
    incidentType: z.enum([
        'injury',
        'illness',
        'property-damage',
        'near-miss',
        'environmental',
        'process-safety',
        'other',
    ]),
    severity: z.enum(['undetermined', 'minor', 'moderate', 'serious', 'critical']),
    reportingDecision: z.enum(['pending-expert-review', 'reportable', 'not-reportable']),
    appliedRuleIds: z.array(entityIdSchema),
    decidedBy: actorSnapshotSchema.nullable(),
    decidedAt: utcDateTimeSchema.nullable(),
    rationale: z.string().trim().max(2_000).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.reportingDecision !== 'pending-expert-review' &&
        (!value.decidedBy || !value.decidedAt || !value.rationale)) {
        context.addIssue({
            code: 'custom',
            path: ['reportingDecision'],
            message: 'A reporting decision requires accountable human review',
        });
    }
});
export const incidentSchema = z
    .object({
    kind: z.literal('incident'),
    recordId: entityIdSchema,
    version: z.number().int().positive(),
    documentVersionId: entityIdSchema,
    scope: resourceScopeSchema,
    stage: workflowStageSchema,
    title: nonEmptyTextSchema.max(300),
    occurredAt: utcDateTimeSchema,
    discoveredAt: utcDateTimeSchema,
    location: nonEmptyTextSchema.max(500),
    contractorId: entityIdSchema.nullable(),
    reporter: actorSnapshotSchema,
    initialNarrative: nonEmptyTextSchema.max(5_000),
    immediateActions: z
        .array(z
        .object({
        actionId: entityIdSchema,
        description: nonEmptyTextSchema.max(1_000),
        actedAt: utcDateTimeSchema,
        actor: actorSnapshotSchema,
    })
        .strict())
        .min(1),
    /** Token into a separately protected vault; names, diagnoses and contact data never live here. */
    affectedPersonRefs: z.array(z
        .object({
        subjectVaultRef: entityIdSchema,
        maskedLabel: nonEmptyTextSchema.max(100),
        relationship: z.enum(['employee', 'contractor', 'visitor', 'other']),
    })
        .strict()),
    classification: incidentClassificationSchema,
    /**
     * An urgent first report is accepted before a safety manager applies the published legal
     * classification standard. Every later workflow stage still requires the exact snapshot.
     */
    reportingStandard: reportingStandardSnapshotSchema.nullable(),
    externalReports: z.array(z
        .object({
        reportId: entityIdSchema,
        authority: nonEmptyTextSchema.max(300),
        ruleId: entityIdSchema,
        transmittedAt: utcDateTimeSchema,
        receiptReference: nonEmptyTextSchema.max(500),
        transmittedBy: actorSnapshotSchema,
    })
        .strict()),
    investigation: z
        .object({
        investigators: z.array(actorSnapshotSchema).min(1),
        facts: z.array(nonEmptyTextSchema.max(2_000)).min(1),
        directCauses: z.array(nonEmptyTextSchema.max(1_000)).min(1),
        contributingCauses: z.array(nonEmptyTextSchema.max(1_000)),
        rootCauses: z.array(nonEmptyTextSchema.max(1_000)).min(1),
        completedAt: utcDateTimeSchema,
    })
        .strict()
        .nullable(),
    actions: z.array(actionSchema),
    evidence: z.array(evidenceSchema),
    reviews: z.array(reviewSchema),
    lock: z
        .object({
        contentHash: sha256Schema,
        lockedAt: utcDateTimeSchema,
        lockedBy: actorSnapshotSchema,
        lockedVersion: z.number().int().positive(),
    })
        .strict()
        .nullable(),
    legacy: z
        .object({
        incidentCollectionId: entityIdSchema,
        monitoringCollectionId: entityIdSchema.nullable(),
    })
        .strict()
        .nullable(),
    createdBy: actorSnapshotSchema,
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.reportingStandard === null) {
        const firstReportOnly = value.stage === 'draft' &&
            value.classification.severity === 'undetermined' &&
            value.classification.reportingDecision === 'pending-expert-review' &&
            value.classification.appliedRuleIds.length === 0 &&
            value.classification.decidedBy === null &&
            value.classification.decidedAt === null &&
            value.classification.rationale === null &&
            value.externalReports.length === 0 &&
            value.investigation === null &&
            value.reviews.length === 0 &&
            value.lock === null;
        if (!firstReportOnly)
            context.addIssue({
                code: 'custom',
                path: ['reportingStandard'],
                message: 'Only an unclassified urgent first-report draft may omit the published standard',
            });
        return;
    }
    const availableRules = new Set(value.reportingStandard.ruleReferences.map((rule) => rule.ruleId));
    value.classification.appliedRuleIds.forEach((ruleId, index) => {
        if (!availableRules.has(ruleId))
            context.addIssue({
                code: 'custom',
                path: ['classification', 'appliedRuleIds', index],
                message: 'Applied rule must exist in the pinned reporting standard',
            });
    });
    value.externalReports.forEach((report, index) => {
        if (!availableRules.has(report.ruleId))
            context.addIssue({
                code: 'custom',
                path: ['externalReports', index, 'ruleId'],
                message: 'External report must cite the pinned reporting standard',
            });
    });
});
const drillStepSchema = z
    .object({
    stepId: entityIdSchema,
    order: z.number().int().positive(),
    plannedOffsetMinutes: z.number().int().nonnegative().nullable(),
    instruction: nonEmptyTextSchema.max(2_000),
    responsibleRoleId: entityIdSchema,
    observedAt: utcDateTimeSchema.nullable(),
    observation: z.string().trim().max(2_000).nullable(),
})
    .strict();
export const emergencyDrillSchema = z
    .object({
    kind: z.literal('emergency-drill'),
    recordId: entityIdSchema,
    version: z.number().int().positive(),
    documentVersionId: entityIdSchema,
    scope: resourceScopeSchema,
    stage: workflowStageSchema,
    title: nonEmptyTextSchema.max(300),
    drillType: nonEmptyTextSchema.max(200),
    purpose: nonEmptyTextSchema.max(2_000),
    plannedStartAt: utcDateTimeSchema,
    plannedEndAt: utcDateTimeSchema,
    location: nonEmptyTextSchema.max(500),
    targetDescription: nonEmptyTextSchema.max(1_000),
    scenarioStandard: z
        .object({
        templateId: entityIdSchema,
        versionId: entityIdSchema,
        title: nonEmptyTextSchema.max(300),
        publishedAt: utcDateTimeSchema,
    })
        .strict(),
    layoutMap: z
        .object({
        mapId: entityIdSchema,
        versionId: entityIdSchema,
        title: nonEmptyTextSchema.max(300),
        contentHash: sha256Schema,
    })
        .strict(),
    roles: z
        .array(z
        .object({
        roleId: entityIdSchema,
        roleName: nonEmptyTextSchema.max(200),
        assigneeIdentityId: entityIdSchema,
        responsibilities: z.array(nonEmptyTextSchema.max(1_000)).min(1),
    })
        .strict())
        .min(1),
    emergencyContactsSnapshotId: entityIdSchema,
    steps: z.array(drillStepSchema).min(1),
    execution: z
        .object({
        actualStartAt: utcDateTimeSchema,
        actualEndAt: utcDateTimeSchema,
        participantCount: z.number().int().nonnegative(),
        attendanceEvidenceIds: z.array(entityIdSchema).min(1),
        timeline: z
            .array(z
            .object({
            eventId: entityIdSchema,
            occurredAt: utcDateTimeSchema,
            description: nonEmptyTextSchema.max(2_000),
            recordedBy: actorSnapshotSchema,
        })
            .strict())
            .min(1),
        result: z.enum(['achieved', 'partially-achieved', 'not-achieved']),
        strengths: z.array(nonEmptyTextSchema.max(1_000)),
        gaps: z.array(nonEmptyTextSchema.max(1_000)),
        evaluator: actorSnapshotSchema,
    })
        .strict()
        .nullable(),
    actions: z.array(actionSchema),
    evidence: z.array(evidenceSchema),
    reviews: z.array(reviewSchema),
    lock: z
        .object({
        contentHash: sha256Schema,
        lockedAt: utcDateTimeSchema,
        lockedBy: actorSnapshotSchema,
        lockedVersion: z.number().int().positive(),
    })
        .strict()
        .nullable(),
    legacy: z.object({ emergencyDrillId: entityIdSchema }).strict().nullable(),
    createdBy: actorSnapshotSchema,
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.plannedEndAt) <= Date.parse(value.plannedStartAt))
        context.addIssue({
            code: 'custom',
            path: ['plannedEndAt'],
            message: 'Drill end must follow start',
        });
    if (value.execution &&
        Date.parse(value.execution.actualEndAt) <= Date.parse(value.execution.actualStartAt))
        context.addIssue({
            code: 'custom',
            path: ['execution', 'actualEndAt'],
            message: 'Actual end must follow start',
        });
});
export const safetyEventRecordSchema = z
    .discriminatedUnion('kind', [incidentSchema, emergencyDrillSchema])
    .superRefine((value, context) => {
    if (['submitted', 'in-review', 'resubmitted', 'approved', 'action-tracking', 'closed'].includes(value.stage)) {
        value.evidence.forEach((item, index) => {
            if (!item.serverCommitted || !item.immutable)
                context.addIssue({
                    code: 'custom',
                    path: ['evidence', index],
                    message: 'Submitted evidence must be committed and immutable',
                });
        });
    }
    if (['approved', 'action-tracking', 'closed'].includes(value.stage) !== (value.lock !== null))
        context.addIssue({
            code: 'custom',
            path: ['lock'],
            message: 'Approved records require an integrity lock',
        });
});
